// src/email/socket/useEmailSocket.js
import { useEffect } from 'react';
import { io } from 'socket.io-client';
import {
  getStoredEmailToken,
  ensureEmailAuth,
  getCurrentCrmUser,
  EMAIL_API_URL,
  emailApiClient,
  EMAIL_APIS_DISABLED,
} from '../api/client';

let globalSocket = null;
let connectionPromise = null;
let subscribersCount = 0;
const qcSubscribers = new Set();
const recentlyNotified = new Map(); // key -> timestamp

/**
 * Connects to the email backend's /email Socket.IO namespace and keeps
 * react-query cache and sidebar menus fresh in real time, and fires desktop notifications.
 */
export function useEmailSocket(queryClient = null) {
  useEffect(() => {
    if (EMAIL_APIS_DISABLED) {
      if (globalSocket) {
        try {
          globalSocket.disconnect();
        } catch (_) {}
        globalSocket = null;
      }
      return;
    }
    let isMounted = true;
    if (queryClient) {
      qcSubscribers.add(queryClient);
    }
    subscribersCount += 1;

    async function initSocket() {
      if (globalSocket && globalSocket.connected) {
        return globalSocket;
      }
      if (connectionPromise) {
        return connectionPromise;
      }

      connectionPromise = (async () => {
        try {
          let token = getStoredEmailToken();
          if (!token) {
            token = await ensureEmailAuth();
          }
          if (!token || !isMounted) return null;

          if (!globalSocket || !globalSocket.connected) {
            const socket = io(`${EMAIL_API_URL}/email`, {
              auth: { token, crmUser: getCurrentCrmUser() },
              transports: ['websocket', 'polling'],
            });

            const invalidateAccounts = () => {
              qcSubscribers.forEach((qc) => {
                try {
                  qc.invalidateQueries({ queryKey: ['email-accounts'] });
                } catch (_) {}
              });
              window.dispatchEvent(new CustomEvent('emailAccountsUpdated'));
            };

            const invalidateThreads = () => {
              qcSubscribers.forEach((qc) => {
                try {
                  qc.invalidateQueries({ queryKey: ['threads'] });
                  qc.invalidateQueries({ queryKey: ['thread'] });
                } catch (_) {}
              });
              window.dispatchEvent(new CustomEvent('emailAccountsUpdated'));
            };

            const handleNewMessage = async (data) => {
              // Always keep threads fresh
              invalidateThreads();

              const notifKey = data?.messageId || data?.threadId;
              const now = Date.now();

              // 🛡️ DEDUPLICATION: Ignore duplicate alerts for the same message/thread within 15 seconds
              if (notifKey) {
                const lastTime = recentlyNotified.get(notifKey);
                if (lastTime && now - lastTime < 15000) {
                  return;
                }
                recentlyNotified.set(notifKey, now);

                // Prune map if large
                if (recentlyNotified.size > 100) {
                  for (const [k, t] of recentlyNotified.entries()) {
                    if (now - t > 60000) recentlyNotified.delete(k);
                  }
                }
              }

              let fromName = data?.from;
              let emailSubject = data?.subject;
              let snippet = data?.snippet;

              // If from or subject is not directly in the socket payload, fetch thread details
              if ((!fromName || !emailSubject) && data?.threadId) {
                for (let attempt = 0; attempt < 2; attempt++) {
                  try {
                    const res = await emailApiClient.get(`/email/threads/${data.threadId}`);
                    if (res.data) {
                      const thread = res.data;
                      if (thread.subject) emailSubject = thread.subject;
                      const msgs = thread.messages;
                      const latestMsg =
                        Array.isArray(msgs) && msgs.length > 0 ? msgs[msgs.length - 1] : null;
                      if (latestMsg && latestMsg.fromAddress) {
                        fromName = latestMsg.fromAddress;
                        snippet = latestMsg.snippet || thread.snippet || snippet;
                        break;
                      }
                    }
                  } catch (err) {
                    console.debug('[Email Socket] Failed to fetch thread details for notification:', err);
                  }

                  // If DB write is still completing, pause briefly before retrying
                  if (!fromName || !emailSubject) {
                    await new Promise((r) => setTimeout(r, 350));
                  }
                }
              }

              // Clean sender name (e.g. "John Doe <john@gmail.com>" -> "John Doe")
              let cleanSender = '';
              if (fromName) {
                cleanSender = fromName.replace(/<.*?>/, '').trim().replace(/^["']|["']$/g, '');
                if (!cleanSender) {
                  const match = fromName.match(/<([^>]+)>/);
                  cleanSender = match ? match[1] : fromName;
                }
              }

              const notifTitle = cleanSender ? `📩 ${cleanSender}` : '📩 New Email Received';
              const notifBody = emailSubject
                ? (snippet ? `${emailSubject}\n${snippet.slice(0, 90)}` : emailSubject)
                : (snippet ? snippet.slice(0, 90) : 'You have received a new email in your CRM mailbox.');

              // Native browser desktop pop-up notification with proper CRM branding
              if (
                typeof window !== 'undefined' &&
                'Notification' in window &&
                Notification.permission === 'granted'
              ) {
                try {
                  const iconUrl = `${window.location.origin}/logo1.png`;
                  const badgeUrl = `${window.location.origin}/favicon.ico`;
                  const targetUrl = data?.threadId ? `/email?threadId=${data.threadId}` : '/email';

                  const notifOptions = {
                    body: notifBody,
                    icon: iconUrl,
                    badge: badgeUrl,
                    tag: notifKey ? `email-thread-${notifKey}` : `email-alert-${now}`,
                    renotify: false,
                    requireInteraction: false,
                    data: {
                      url: targetUrl,
                    },
                  };

                  // Use ServiceWorker registration when available (gives crisp native OS display & logo support)
                  if ('serviceWorker' in navigator) {
                    try {
                      const reg = await navigator.serviceWorker.ready;
                      if (reg && reg.showNotification) {
                        await reg.showNotification(notifTitle, notifOptions);
                        return;
                      }
                    } catch (_) {}
                  }

                  // Fallback to Window Notification API
                  const notif = new Notification(notifTitle, notifOptions);
                  notif.onclick = () => {
                    window.focus();
                    window.location.href = targetUrl;
                  };
                } catch (err) {
                  console.debug('[Email Socket] Desktop notification error:', err);
                }
              }
            };

            socket.on('email:counts_updated', () => {
              invalidateAccounts();
              invalidateThreads();
            });
            socket.on('email:new_message', handleNewMessage);
            socket.on('email:message_updated', invalidateThreads);
            socket.on('email:thread_updated', invalidateThreads);
            socket.on('email:account_status', invalidateAccounts);

            globalSocket = socket;
          }
          return globalSocket;
        } finally {
          connectionPromise = null;
        }
      })();

      return connectionPromise;
    }

    const handleAccountSelected = (e) => {
      if (globalSocket && globalSocket.connected && e?.detail) {
        try {
          globalSocket.emit('account:subscribe', e.detail);
        } catch (_) {}
      }
    };

    window.addEventListener('emailAccountSelected', handleAccountSelected);
    initSocket();

    return () => {
      isMounted = false;
      window.removeEventListener('emailAccountSelected', handleAccountSelected);
      if (queryClient) {
        qcSubscribers.delete(queryClient);
      }
      subscribersCount -= 1;
      if (subscribersCount <= 0 && globalSocket) {
        globalSocket.disconnect();
        globalSocket = null;
        subscribersCount = 0;
      }
    };
  }, [queryClient]);
}



